import {Grid} from "@material-ui/core";
import React from "react";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {mainCategories} from "../../../../data/main-categories";
import {BoxWrapper} from "../../../box-wrapper/box-wrapper";
import {Categories} from "../../../categories";
import {StyledInput} from "../../../form/input";
import {StyledSelect} from "../../../form/select";
import {TagsSelect} from "../../../form/tags-select";
import UploadDropzone from "../../../form/upload-dropzone";
import {StyledRadioButton} from "../../../form/radio";
import {LanguageAutocomplete} from "../../../form/language-autocomplete";
import {CountryAutocomplete} from "../../../form/country-autocomplete";
import {newMainCategories} from "../../../../data/new-main-categories";

export const AddBookFormStep1: React.FC<Props> = (
	{
		contentFile,
		setContentFile,
		contentCover,
		setContentCover,
		handleChange,
		setFieldValue,
		values,
		errors,
		touched
	}
) => {
	const {t} = useTranslation(['publisher', 'common']);

	return (
		<Grid
			container
			direction="row"
			spacing={1}
		>
			<Grid container item sm={12} md={6} direction={'column'}>
				<Grid container item direction={'column'} spacing={2}>

					<Grid item>
						<StyledRadioButton
							label={t('addBookNew.form.bookStatus')}
							options={[{
								label: t('addBookNew.form.bookStatusLabel1'),
								value: 'CREATE'
							}, {
								label: t('addBookNew.form.bookStatusLabel2'),
								value: 'PUBLISH'
							}, {
								label: t('addBookNew.form.bookStatusLabel3'),
								value: 'MARKET'
							}]}
							value={values.bookStatus}
							onChange={(event) => {
								setFieldValue('bookStatus', event.target.value);
							}}
							direction={'row'}
						/>
					</Grid>

					<Grid container item direction={'column'}>
						<StyledInput
							id="isbn"
							label={t('addBookNew.form.isbn')}
							onChange={handleChange}
							value={values.isbn}
							error={errors.isbn && touched.isbn}
						/>
					</Grid>

					<Grid container item direction={'column'}>
						<StyledInput
							id="author"
							label={t('addBookNew.form.author') + ' *'}
							onChange={handleChange}
							value={values.author}
							error={errors.author && touched.author}
						/>
					</Grid>

					<Grid item>
						<TagsSelect
						    setFieldValue={setFieldValue}
							values={values}
						    fieldName={'tags'}
						    label={t('addBookNew.form.tags')}
						    error={!!(errors.tags && touched.tags)}
						/>
					</Grid>

					<Grid item style={{maxWidth: '100%'}}>
						<StyledSelect
							id={'mainCategory'}
							label={t('addBookNew.form.mainCategory') + " *"}
							onChange={handleChange}
							value={values.mainCategory}
							options={newMainCategories}
							optionsLabel={(option) => t('common:mainCategories.' + option)}
							error={errors.mainCategory && touched.mainCategory}
						/>
					</Grid>

					{values.mainCategory &&
                        <Grid item style={{maxWidth: '100%'}}>
                            <BoxWrapper title={t('addBookNew.form.categories') + ' *'}>
								{errors.categories && touched.categories &&
                                    <StyledErrorP>{t('addBookNew.form.categoriesError')}</StyledErrorP>
								}
                                <Categories
                                    selectedCategory={values.categories}
                                    mainCategory={values.mainCategory}
                                    handleSelectedCategory={(selectedCategory) => {
										setFieldValue('categories', selectedCategory);
									}}
                                />
                            </BoxWrapper>
                        </Grid>
					}
				</Grid>
			</Grid>

			<Grid container item sm={12} md={6}>
				<Grid container item direction={'column'} spacing={3}>

					<Grid container item direction={'column'}>
						<StyledInput
							id="title"
							label={t('addBookNew.form.title') + ' *'}
							onChange={handleChange}
							value={values.title}
							error={errors.title && touched.title}
						/>
					</Grid>

					<Grid container item direction={'column'}>
						<StyledInput
							id="titleEn"
							label={t('addBookNew.form.titleEn') + ' *'}
							onChange={handleChange}
							value={values.titleEn}
							error={errors.titleEn && touched.titleEn}
						/>
					</Grid>

					<Grid item>
						<Grid item>
							<LanguageAutocomplete
								value={values.originalLang}
								setFieldValue={setFieldValue}
								fieldName={'originalLang'}
								label={t('addBookNew.form.originalLang') + ' *'}
								handleChange={handleChange}
								error={!!(errors.originalLang && touched.originalLang)}
								multiple={false}
							/>
						</Grid>
					</Grid>

					<Grid item>
						<LanguageAutocomplete
							value={values.translatedTo}
							setFieldValue={setFieldValue}
							fieldName={'translatedTo'}
							label={t('addBookNew.form.translatedTo')}
							handleChange={handleChange}
							error={!!(errors.translatedTo && touched.translatedTo)}
						/>
					</Grid>

					<Grid item>
						<CountryAutocomplete
							values={values}
							setFieldValue={setFieldValue}
							fieldName={'countryWithLicense'}
							label={t('addBookNew.form.countryWithLicense')}
							error={!!(errors.countryWithLicense && touched.countryWithLicense)}
						/>
					</Grid>

					<Grid item>
						<UploadDropzone
							setFieldValue={setFieldValue}
							values={values}
							fieldName={'file'}
							inputAccept={'application/pdf,.txt'}
							description={t('addBookNew.form.uploadDesc')}
							initialValue={contentFile}
							setInitialValue={setContentFile}
							info={t('addBookNew.form.uploadInfo')}
							error={false}
							wrongFormatError={errors.file && touched.file}
						/>
					</Grid>

					<Grid item>
						<UploadDropzone
							setFieldValue={setFieldValue}
							values={values}
							fieldName={'cover'}
							inputAccept={'image/*'}
							description={t('addBookNew.form.uploadCoverDesc')}
							initialValue={contentCover}
							setInitialValue={setContentCover}
							info={t('addBookNew.form.uploadCoverInfo')}
							error={false}
							wrongFormatError={errors.cover && touched.cover}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

const StyledErrorP = styled('p')`
  color: #f44336;
  margin-top: -5px;
  margin-bottom: 10px;
`;

interface Props {
	contentFile?: any;
	setContentFile?: any;
	contentCover?: any;
	setContentCover?: any;
	handleChange: any;
	setFieldValue: any;
	values: any;
	errors: any;
	touched: any;
}
